import { StandardEvents } from '@shopify/events';

const GUARD_KEY = '__plVariantCheckoutGuardInstalled';
const generations = new WeakMap();

function getScope(event) {
  const source = event.target instanceof Element ? event.target : null;
  return source?.closest('product-card, dialog, .shopify-section') || document;
}

function getProductForms(event) {
  const scope = getScope(event);
  return Array.from(scope.querySelectorAll('product-form-component'));
}

function getVariantInput(form) {
  return form.querySelector('input[ref="variantId"][name="id"]');
}

function getAcceleratedCheckout(form) {
  return form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
}

function setCheckoutLocked(form, locked, failed = false) {
  const checkout = getAcceleratedCheckout(form);
  form.toggleAttribute('data-variant-checkout-syncing', locked && !failed);
  form.toggleAttribute('data-variant-checkout-failed', failed);

  if (!checkout) return;

  checkout.toggleAttribute('data-variant-syncing', locked && !failed);
  checkout.toggleAttribute('data-variant-sync-failed', failed);
  checkout.inert = locked || failed;

  if (locked || failed) {
    checkout.setAttribute('aria-busy', locked ? 'true' : 'false');
    checkout.setAttribute(
      'title',
      failed
        ? 'Nie udało się potwierdzić wybranego wariantu. Wybierz wariant ponownie.'
        : 'Aktualizujemy wybrany wariant…'
    );
  } else {
    checkout.removeAttribute('aria-busy');
    checkout.removeAttribute('title');
  }
}

function clearStaleVariantId(form) {
  const input = getVariantInput(form);
  if (input) input.value = '';
}

function findFreshCheckout(form, html) {
  if (!html) return null;

  const liveProductForm = form.querySelector('form[data-type="add-to-cart-form"]');
  const liveFormId = liveProductForm?.id;

  if (liveFormId && typeof html.getElementById === 'function') {
    const renderedForm = html.getElementById(liveFormId);
    const renderedCheckout = renderedForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (renderedCheckout) return renderedCheckout;
  }

  const productId = form.dataset.productId;
  const renderedProductForms = Array.from(html.querySelectorAll?.('product-form-component') || []);
  const matchingForm = renderedProductForms.find((node) => !productId || node.dataset.productId === productId);
  return matchingForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]') || null;
}

function syncAuthoritativeVariant(form, detail) {
  const resource = detail?.resource;
  const expectedVariantId = resource?.id != null ? String(resource.id) : '';
  const input = getVariantInput(form);

  if (input) {
    const changed = input.value !== expectedVariantId;
    input.value = expectedVariantId;
    if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const currentCheckout = getAcceleratedCheckout(form);
  const freshCheckout = findFreshCheckout(form, detail?.html);

  if (currentCheckout && freshCheckout) {
    currentCheckout.innerHTML = freshCheckout.innerHTML;
    currentCheckout.hidden = freshCheckout.hidden;

    // Legacy themes may expose this initializer; modern web components initialize on connect.
    window.Shopify?.PaymentButton?.init?.();
  } else if (currentCheckout && (resource == null || resource.available === false)) {
    currentCheckout.hidden = true;
  }
}

function disableAddToCartAfterHardFailure(form) {
  const addToCart = form.querySelector('[ref="addToCartButton"]');
  if (addToCart instanceof HTMLButtonElement) addToCart.disabled = true;
}

async function handleProductSelect(event) {
  if (!event?.promise) return;

  const forms = getProductForms(event);
  if (!forms.length) return;

  const tracked = forms.map((form) => {
    const generation = (generations.get(form) || 0) + 1;
    generations.set(form, generation);

    // Fail closed: never leave the previous variant ID in the form while the new
    // option combination is still being resolved by Shopify.
    clearStaleVariantId(form);
    setCheckoutLocked(form, true, false);

    return { form, generation };
  });

  try {
    const result = await event.promise;
    const detail = result?.detail;

    if (!detail?.html) throw new Error('Variant response did not include rendered section HTML.');

    // ProductFormComponent also awaits this promise. Yield one microtask so its
    // own authoritative state update completes before the accelerated checkout
    // button is unlocked.
    await Promise.resolve();

    for (const { form, generation } of tracked) {
      if (generations.get(form) !== generation) continue;

      syncAuthoritativeVariant(form, detail);
      form.removeAttribute('data-variant-checkout-failed');
      setCheckoutLocked(form, false, false);
    }
  } catch (error) {
    for (const { form, generation } of tracked) {
      if (generations.get(form) !== generation) continue;

      // Keep the old variant impossible to submit. A fresh option selection will
      // retry resolution and restore the buttons after Shopify confirms it.
      clearStaleVariantId(form);
      setCheckoutLocked(form, false, true);
      disableAddToCartAfterHardFailure(form);
    }

    if (error?.name !== 'AbortError') {
      console.warn('[PL variant checkout guard] Variant confirmation failed; checkout remains locked.', error);
    }
  }
}

if (!window[GUARD_KEY]) {
  window[GUARD_KEY] = true;
  document.addEventListener(StandardEvents.productSelect, handleProductSelect);
}
